import {
    useInfiniteQuery,
    UseInfiniteQueryResult,
    useMutation,
    UseMutationResult,
    useQuery,
    UseQueryResult,
} from '@tanstack/react-query';
import {decamelizeKeys} from 'humps';
import {useMemo} from 'react';
import {useHistory} from 'react-router';
import {z as r} from 'zod';

import {Locale} from '../../provider/locale/localization';
import {useSnackbar} from '../../provider/snackbar/snackbar-hook';
import {fetchAndDeserialize, getUrlParameters} from '../../util/api-adapter';
import {dropFetchXCache, FetchMethodEnum, fetchNoContent, fetchX} from '../../util/fetch';
import {usePagination} from '../../util/pagination-hook/pagination-hook';
import {PaginationType} from '../../util/pagination-hook/pagination-hook-type';
import {objectToUrlParameters} from '../../util/url';
import {getCsrfHeaders, mainApiHeaders, rootApiUrl} from '../api/api-const';
import {generateResponseSchema, PaginatedResponseType} from '../api/api-type';

const tagGroupSchema = r.object({
    id: r.number(),
    title: r.string(),
    isRequired: r.boolean(),
});

const tagGroupListSchema = r.array(tagGroupSchema);

export type TagGroupType = r.infer<typeof tagGroupSchema>;

const tagDataSchema = r.object({
    created_at: r.string().optional(),
    folder: tagGroupSchema.nullable(),
    id: r.number(),
    isComplex: r.boolean().optional(),
    keys: r.array(r.string()),
    keywords: r.array(r.string()),
    subTags: r.array(r.unknown()).optional(),
    title: r.string(),
    type: r.string(),
    user: r.object({
        id: r.number(),
    }),
});

const tagsDataSchema = generateResponseSchema(tagDataSchema);

export type TagDataType = r.infer<typeof tagDataSchema>;

const aspectSchema = r.object({
    ids: r.array(r.number()),
    title: r.string(),
});

const qrCodeTagsSchema = r.record(r.string());

type AspectType = r.infer<typeof aspectSchema>;

type QrCodeTagsType = r.infer<typeof qrCodeTagsSchema>;

const tagsWithGroupSchema = r.record(r.record(r.string()));

type TagsWithGroupType = r.infer<typeof tagsWithGroupSchema>;

type DeleteTagRequestType = {
    ids: Array<string> | number;
};

type DeleteTagGroupRequestType = {
    ids: Array<string> | number;
};

export type ChangeTagGroupRequestType = {
    ids: Array<string>;
    destination_folder_id?: number;
    without_folder?: boolean;
};

type ChangeTagTypeRequestType = {
    ids: Array<string>;
    type: string;
};

type CreateGroupRequestType = {
    title: string;
    isRequired?: boolean;
};

type ReviewResultsOptionType = {
    page?: number;
    count?: number;
    folderIds: string;
    tagType: string;
    withoutFolder: true | null;
};

type ReviewTagsOptionType = {
    q?: string;
    folderIds?: number;
    withoutRg?: boolean;
    withoutFolder: true | null;
};

export const defaultTagData: TagDataType = {
    created_at: '0000-00-00T00:00:00.000000Z',
    folder: null,
    id: -1,
    isComplex: false,
    keys: [],
    keywords: [],
    subTags: [],
    title: '',
    type: 'public',
    user: {
        id: 1,
    },
};

export const defaultGroupData: TagGroupType = {
    id: -1,
    title: '',
    isRequired: false,
};

const tagGroupListUrl = '/cp/review_tags/folders/';

function fetchTagGroupList(): Promise<Array<TagGroupType>> {
    return fetchAndDeserialize<Array<TagGroupType>>(tagGroupListUrl, tagGroupListSchema);
}

export function useTagGroupList(isOpen?: boolean): UseQueryResult<Array<TagGroupType>> {
    return useQuery([tagGroupListUrl], () => fetchTagGroupList(), {
        refetchOnWindowFocus: false,
        enabled: isOpen,
    });
}

function getReviewTagsUrl(options: ReviewResultsOptionType | ReviewTagsOptionType) {
    return `/cp/review_tags/${getUrlParameters(options)}`;
}

function fetchTags(
    options: ReviewResultsOptionType | ReviewTagsOptionType
): Promise<PaginatedResponseType<TagDataType>> {
    return fetchAndDeserialize<PaginatedResponseType<TagDataType>>(getReviewTagsUrl(options), tagsDataSchema, {
        fetchOptions: {skipCache: true},
    });
}

export function useTags(
    folderIdList: Array<string>,
    typeTagList: Array<string>,
    withoutFolder: true | null
): PaginationType & UseQueryResult<PaginatedResponseType<TagDataType>> {
    const dependencies = useMemo(
        () => ({
            folderIds: folderIdList.join(','),
            tagType: typeTagList.join(','),
            withoutFolder,
        }),
        [folderIdList, typeTagList, withoutFolder]
    );
    const routerHistory = useHistory<Location>();
    const pagination = usePagination({
        dependencies: null,
        initialPageSize: 20,
        shouldSaveState:
            !routerHistory.location.pathname.includes('create') && !routerHistory.location.pathname.includes('edit'),
    });

    const {page, pageSize, onDataLoaded} = pagination;

    const queryParameters = useMemo(
        () => ({
            page,
            count: pageSize,
            ...dependencies,
        }),
        [page, pageSize, dependencies]
    );

    const query = useQuery([getReviewTagsUrl(queryParameters)], () => fetchTags(queryParameters), {
        onSuccess: (data) => onDataLoaded(data),
        retry: false,
        refetchOnMount: false,
        keepPreviousData: true,
        refetchOnWindowFocus: false,
        retryOnMount: false,
    });

    return {...query, pagination};
}

export function useInfiniteTags(
    options: ReviewTagsOptionType,
    enabled: boolean
): UseInfiniteQueryResult<PaginatedResponseType<TagDataType>> {
    return useInfiniteQuery(
        [getReviewTagsUrl({...options, count: 20})],
        async ({pageParam: page = 1}) => fetchTags({page, ...options, count: 20}),
        {
            getPreviousPageParam: (firstPage) => (firstPage.previous && firstPage.page ? firstPage.page - 1 : 1),
            getNextPageParam: (lastPage) => (lastPage.next && lastPage.page ? lastPage.page + 1 : null),
            refetchOnReconnect: false,
            refetchOnWindowFocus: false,
            refetchInterval: 0,
            retry: 0,
            retryOnMount: false,
            enabled,
        }
    );
}

function fetchAspects(): Promise<Array<AspectType>> {
    return fetchAndDeserialize('/cp/review_tags/aspects/', aspectSchema.array());
}

function fetchQRCodesTags(): Promise<QrCodeTagsType> {
    return fetchAndDeserialize('/cp/review_tags/qr_codes/', qrCodeTagsSchema);
}

function fetchTagsWithGroup(): Promise<TagsWithGroupType> {
    return fetchAndDeserialize('/cp/review_tags/tags_groups/', tagsWithGroupSchema, {
        deserializeOptions: {ignorePaths: [/.*/]},
    });
}

export function useReviewsAspects(enabled: boolean): UseQueryResult<Array<AspectType>> {
    return useQuery(['/cp/review_tags/aspects'], () => fetchAspects(), {
        retry: false,
        refetchOnMount: false,
        keepPreviousData: true,
        refetchOnWindowFocus: false,
        retryOnMount: false,
        enabled,
    });
}

export function useQRCodesTags(): UseQueryResult<QrCodeTagsType> {
    return useQuery(['/cp/review_tags/qr_codes'], () => fetchQRCodesTags(), {
        retry: false,
        refetchOnMount: false,
        keepPreviousData: true,
        refetchOnWindowFocus: false,
        retryOnMount: false,
    });
}

export function useTagsWithGroup(): UseQueryResult<TagsWithGroupType> {
    return useQuery(['/cp/review_tags/tags_groups'], () => fetchTagsWithGroup(), {
        retry: false,
        refetchOnMount: false,
        keepPreviousData: true,
        refetchOnWindowFocus: false,
        retryOnMount: false,
    });
}

function getTagUrl(id: number) {
    return `/cp/review_tags/${id}/`;
}

function fetchTag(id: number): Promise<TagDataType> {
    return fetchAndDeserialize<TagDataType>(getTagUrl(id), tagDataSchema);
}

export function useTag(idTag: number): UseQueryResult<TagDataType> {
    return useQuery([getTagUrl(idTag)], () => fetchTag(idTag), {
        refetchOnWindowFocus: false,
    });
}

export function fetchTagCreate(data: TagDataType): Promise<TagDataType> {
    return fetchX<TagDataType>(rootApiUrl + '/cp/review_tags/', {
        method: FetchMethodEnum.post,
        body: JSON.stringify(data),
        headers: {...mainApiHeaders, ...getCsrfHeaders()},
    });
}

export function fetchTagUpdate(data: TagDataType, id: number): Promise<TagDataType> {
    return fetchX<TagDataType>(`${rootApiUrl}/cp/review_tags/${id}/`, {
        method: FetchMethodEnum.put,
        body: JSON.stringify(data),
        headers: {...mainApiHeaders, ...getCsrfHeaders()},
    });
}

export function fetchTagChangeGroup(data: ChangeTagGroupRequestType): Promise<TagDataType> {
    return fetchX<TagDataType>(`${rootApiUrl}/cp/review_tags/change_tags_folder/`, {
        method: FetchMethodEnum.post,
        body: JSON.stringify(data),
        headers: {...mainApiHeaders, ...getCsrfHeaders()},
    });
}

export function fetchTagChangeType(data: ChangeTagTypeRequestType): Promise<TagDataType> {
    return fetchX<TagDataType>(`${rootApiUrl}/cp/review_tags/change_tags_type/`, {
        method: FetchMethodEnum.post,
        body: JSON.stringify(data),
        headers: {...mainApiHeaders, ...getCsrfHeaders()},
    });
}

export function fetchTagDelete(data: DeleteTagRequestType): Promise<TagDataType> {
    const parameters = objectToUrlParameters(data);

    return fetchX<TagDataType>(`${rootApiUrl}/cp/review_tags/?${parameters}`, {
        method: FetchMethodEnum.delete,
        headers: {...mainApiHeaders, ...getCsrfHeaders()},
    });
}

export function fetchGroupCreate(data: CreateGroupRequestType): Promise<TagGroupType> {
    return fetchX<TagGroupType>(rootApiUrl + '/cp/review_tags/folders/', {
        method: FetchMethodEnum.post,
        body: JSON.stringify(data),
        headers: {...mainApiHeaders, ...getCsrfHeaders()},
    });
}

export function fetchGroupUpdate(data: TagGroupType): Promise<TagGroupType> {
    return fetchX<TagGroupType>(`${rootApiUrl}/cp/review_tags/folders/${data.id}/`, {
        method: FetchMethodEnum.put,
        body: JSON.stringify(decamelizeKeys(data)),
        headers: {...mainApiHeaders, ...getCsrfHeaders()},
    });
}

export function fetchGroupDelete(data: DeleteTagGroupRequestType): Promise<void> {
    dropFetchXCache([`${rootApiUrl}/cp/review_tags/folders/ - ${JSON.stringify('[empty]')}`]);

    const parameters = objectToUrlParameters(data);

    return fetchNoContent(`${rootApiUrl}/cp/review_tags/folders/?${parameters}`, {
        method: FetchMethodEnum.delete,
        headers: {...mainApiHeaders, ...getCsrfHeaders()},
    });
}

export function useGroupUpdateMutation(): UseMutationResult<TagGroupType, unknown, TagGroupType> {
    const {snackbar} = useSnackbar();

    return useMutation({
        mutationFn: (data: TagGroupType) => fetchGroupUpdate(data),
        onSuccess: () => {
            snackbar.success(<Locale stringKey="TAGS__GROUP__UPDATING_SUCCESS" />);
        },
        onError: () => {
            snackbar.error(<Locale stringKey="TAGS__GROUP__UPDATING_ERROR" />);
        },
    });
}

function fetchRequiredTagGroups() {
    return fetchAndDeserialize('/cp/review_tags/folders/required_groups/', tagGroupListSchema);
}

export function useGetRequiredGroups(): UseQueryResult<Array<TagGroupType>> {
    return useQuery(['/cp/review_tags/folders/required_groups/'], () => fetchRequiredTagGroups());
}
